import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { getTradeJournal } from '@/utils/localStorage';
import { calculateJournalStats, getWeeklyInsights } from '@/utils/calculations';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { AlertTriangle, CheckCircle, Info, TrendingUp, Shield } from 'lucide-react';

export default function WeeklyReview() {
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [emotionChartData, setEmotionChartData] = useState([]);
  const [setupChartData, setSetupChartData] = useState([]);
  
  useEffect(() => {
    const journalData = getTradeJournal();
    setTrades(journalData);
    
    const statsData = calculateJournalStats(journalData);
    setStats(statsData);
    
    const insightsData = getWeeklyInsights(journalData);
    setInsights(insightsData);
    
    // Prepare emotion chart data
    if (statsData.performanceByEmotion) {
      const emotionData = Object.entries(statsData.performanceByEmotion).map(([emotion, data]) => ({
        emotion: emotion.charAt(0).toUpperCase() + emotion.slice(1),
        winRate: parseFloat(data.winRate.toFixed(1)),
        count: data.count
      }));
      setEmotionChartData(emotionData);
    }
    
    // Prepare setup type chart data
    const setupStats = {};
    journalData.forEach(trade => {
      if (trade.setupType) {
        if (!setupStats[trade.setupType]) {
          setupStats[trade.setupType] = { total: 0, wins: 0 };
        }
        setupStats[trade.setupType].total++;
        if (trade.result === 'win') {
          setupStats[trade.setupType].wins++;
        }
      }
    });
    
    const setupData = Object.entries(setupStats).map(([setup, data]) => ({
      setup: setup.charAt(0).toUpperCase() + setup.slice(1),
      winRate: ((data.wins / data.total) * 100).toFixed(1),
      count: data.total
    }));
    setSetupChartData(setupData);
  }, []);
  
  const getBarColor = (value) => {
    if (value >= 60) return '#10B981'; // success
    if (value >= 40) return '#F59E0B'; // warning
    return '#EF4444'; // destructive
  };
  
  const getIconForInsightType = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-success" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-warning" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-destructive" />;
      default:
        return <Info className="w-5 h-5 text-primary" />;
    }
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground mb-2">
          Weekly Review
        </h1>
        <p className="text-muted-foreground font-medium">
          Analyze patterns, identify triggers, and improve your trading discipline.
        </p>
      </div>
      
      {trades.length === 0 ? (
        <Card className="p-12 border-border/40 bg-card text-center">
          <TrendingUp className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-20" />
          <p className="text-muted-foreground font-medium">No trades logged yet. Start logging trades to see your weekly review.</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
            <Card className="col-span-full md:col-span-8 p-6 border-border/40 bg-card">
              <h2 className="text-2xl font-bold text-foreground mb-4">Performance Overview</h2>
              
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center p-6 rounded-md bg-secondary/30">
                  <p className="text-sm text-muted-foreground mb-2">Total Trades</p>
                  <p className="text-5xl font-black font-mono" data-testid="review-total-trades">{stats?.totalTrades || 0}</p>
                </div>
                
                <div className="text-center p-6 rounded-md bg-success/10 border border-success/30">
                  <p className="text-sm text-muted-foreground mb-2">Win Rate</p>
                  <p className="text-5xl font-black font-mono text-success" data-testid="review-win-rate">{stats?.winRate || 0}%</p>
                </div>
                
                <div className="text-center p-6 rounded-md bg-primary/10 border border-primary/30">
                  <p className="text-sm text-muted-foreground mb-2">Avg R:R</p>
                  <p className="text-5xl font-black font-mono text-primary" data-testid="review-avg-rr">{stats?.avgRR || 0}</p>
                </div>
              </div>
              
              {emotionChartData.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-4">Performance by Emotion</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={emotionChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="emotion" stroke="#A1A1AA" />
                      <YAxis stroke="#A1A1AA" label={{ value: 'Win Rate (%)', angle: -90, position: 'insideLeft', fill: '#A1A1AA' }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                        labelStyle={{ color: '#FAFAFA' }}
                      />
                      <Bar dataKey="winRate" radius={[8, 8, 0, 0]}>
                        {emotionChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getBarColor(entry.winRate)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
            
            <Card className="col-span-full md:col-span-4 p-6 border-border/40 bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4">Key Metrics</h2>
              
              <div className="space-y-6">
                <div className="p-4 rounded-md bg-secondary/30">
                  <p className="text-sm text-muted-foreground mb-1">Most Common Mistake</p>
                  <p className="text-lg font-bold text-foreground break-words" data-testid="review-common-mistake">
                    {stats?.mostCommonMistake || 'None'}
                  </p>
                </div>
                
                {stats && (stats.ruleFollowingStats.totalTrades > 0 || stats.ruleBreakingStats.totalTrades > 0) && (
                  <div className="border border-primary/30 rounded-md p-4 bg-primary/5">
                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center">
                      <Shield className="w-4 h-4 mr-2 text-primary" />
                      Trading Plan Adherence
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-foreground">Rule-Following Trades</span>
                        <div className="text-right">
                          <p className="font-mono font-bold text-success" data-testid="rule-following-winrate">
                            {stats.ruleFollowingStats.winRate.toFixed(1)}%
                          </p>
                          <p className="text-xs text-muted-foreground">{stats.ruleFollowingStats.totalTrades} trades</p>
                        </div>
                      </div>
                      
                      {stats.ruleBreakingStats.totalTrades > 0 && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-foreground">Rule-Violation Trades</span>
                            <div className="text-right">
                              <p className="font-mono font-bold text-destructive" data-testid="rule-breaking-winrate">
                                {stats.ruleBreakingStats.winRate.toFixed(1)}%
                              </p>
                              <p className="text-xs text-muted-foreground">{stats.ruleBreakingStats.totalTrades} trades</p>
                            </div>
                          </div>
                          
                          {stats.ruleFollowingStats.winRate > stats.ruleBreakingStats.winRate + 5 && (
                            <div className="mt-3 p-3 rounded bg-warning/10 border border-warning/30">
                              <p className="text-xs font-semibold text-warning">
                                ⚠️ Rule-following trades perform {(stats.ruleFollowingStats.winRate - stats.ruleBreakingStats.winRate).toFixed(1)}% better. Stick to your plan!
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {stats && (stats.plannedTradesStats.totalTrades > 0 || stats.unplannedTradesStats.totalTrades > 0) && (
                  <div className="border border-success/30 rounded-md p-4 bg-success/5">
                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-success" />
                      Pre-Trade Discipline
                    </h3>
                    <div className="space-y-3">
                      {stats.plannedTradesStats.totalTrades > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-foreground">Planned Trades (Pre-Validated)</span>
                          <div className="text-right">
                            <p className="font-mono font-bold text-success" data-testid="planned-winrate">
                              {stats.plannedTradesStats.winRate.toFixed(1)}%
                            </p>
                            <p className="text-xs text-muted-foreground">{stats.plannedTradesStats.totalTrades} trades</p>
                          </div>
                        </div>
                      )}
                      
                      {stats.unplannedTradesStats.totalTrades > 0 && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-foreground">Unplanned Trades (No ID)</span>
                            <div className="text-right">
                              <p className="font-mono font-bold text-warning" data-testid="unplanned-winrate">
                                {stats.unplannedTradesStats.winRate.toFixed(1)}%
                              </p>
                              <p className="text-xs text-muted-foreground">{stats.unplannedTradesStats.totalTrades} trades</p>
                            </div>
                          </div>
                          
                          {stats.plannedTradesStats.totalTrades > 0 && stats.plannedTradesStats.winRate > stats.unplannedTradesStats.winRate + 5 && (
                            <div className="mt-3 p-3 rounded bg-destructive/10 border border-destructive/30">
                              <p className="text-xs font-semibold text-destructive">
                                🚨 Planned trades perform {(stats.plannedTradesStats.winRate - stats.unplannedTradesStats.winRate).toFixed(1)}% better! Stop hindsight logging. Validate BEFORE execution.
                              </p>
                            </div>
                          )}
                          
                          {stats.unplannedTradesStats.totalTrades > stats.plannedTradesStats.totalTrades && (
                            <div className="mt-3 p-3 rounded bg-destructive/10 border border-destructive/30">
                              <p className="text-xs font-semibold text-destructive">
                                ⚠️ {stats.unplannedTradesStats.totalTrades} unplanned vs {stats.plannedTradesStats.totalTrades} planned. You're trading on impulse!
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {setupChartData.length > 0 && (
                  <div>
                    <h3 className="text-sm text-muted-foreground mb-3">Best Setup Type</h3>
                    {setupChartData
                      .sort((a, b) => parseFloat(b.winRate) - parseFloat(a.winRate))
                      .map(setup => (
                        <div key={setup.setup} className="mb-3 p-3 rounded-md bg-secondary/20 border border-border/30">
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-semibold text-foreground">{setup.setup}</span>
                            <span className="font-mono font-bold text-sm" style={{ color: getBarColor(parseFloat(setup.winRate)) }}>
                              {setup.winRate}%
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{setup.count} trades</p>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
          
          {insights.length > 0 && (
            <Card className="p-6 border-border/40 bg-card">
              <h2 className="text-2xl font-bold text-foreground mb-4">Insights & Suggestions</h2>
              
              <div className="space-y-4">
                {insights.map((insight, index) => (
                  <div
                    key={index}
                    data-testid={`insight-${index}`}
                    className={`p-4 rounded-md border flex items-start space-x-3 ${
                      insight.type === 'success' ? 'bg-success/10 border-success/30' :
                      insight.type === 'warning' ? 'bg-warning/10 border-warning/30' :
                      insight.type === 'error' ? 'bg-destructive/10 border-destructive/30' :
                      'bg-primary/10 border-primary/30'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {getIconForInsightType(insight.type)}
                    </div>
                    <p className="text-foreground font-medium flex-1">{insight.message}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
          
          {insights.length === 0 && (
            <Card className="p-8 border-border/40 bg-card text-center">
              <Info className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-20" />
              <p className="text-muted-foreground font-medium">Log more trades to generate personalized insights and suggestions.</p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}